import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Checkbox, Form, theme } from "antd";
import { EProductPlanAdditionalTeaching } from "@devowl-wp/api-real-commerce";
import { __, _i } from "../utils/i18n.js";
const externalLinkProps = {
    target: "_blank",
    rel: "noopener noreferrer"
};
/**
 * Vertical stack of legal acknowledgements shown right above the submit button.
 * Some entries are purely informational (hidden form field kept true so the
 * submit payload stays truthy); others are explicit opt-ins that block submit
 * until checked. Visibility depends on the product plan's additional teachings
 * and the passed customer type. Lives at the cross-shortcode level so both
 * Checkout V2 and Customer Center V2 modals consume the same surface.
 */ const CustomerTermsSection = ({ customerType, seller, manufacturer, additionalTeachings = [] })=>{
    const { token } = theme.useToken();
    const teachingSet = new Set(additionalTeachings.map(({ teaching })=>teaching));
    const isIndividual = customerType === "individual";
    const showCancellationPolicy = teachingSet.has(EProductPlanAdditionalTeaching.CancellationPolicy) && isIndividual;
    const showExclusionOfRevocation = teachingSet.has(EProductPlanAdditionalTeaching.ExclusionOfRevocation) && isIndividual;
    const showNoLegalAdvice = teachingSet.has(EProductPlanAdditionalTeaching.NoLegalAdviceContractWork);
    const showDpaForPayedSupport = teachingSet.has(EProductPlanAdditionalTeaching.DpaForPayedSupport);
    const paddleTermsAnchor = /*#__PURE__*/ _jsx("a", {
        href: seller.terms,
        ...externalLinkProps
    });
    const devowlTermsAnchor = /*#__PURE__*/ _jsx("a", {
        href: manufacturer.terms,
        ...externalLinkProps
    });
    const paddlePrivacyAnchor = /*#__PURE__*/ _jsx("a", {
        href: seller.privacy,
        ...externalLinkProps
    });
    const devowlPrivacyAnchor = /*#__PURE__*/ _jsx("a", {
        href: manufacturer.privacy,
        ...externalLinkProps
    });
    const cancellationAnchor = /*#__PURE__*/ _jsx("a", {
        href: manufacturer.cancellation,
        ...externalLinkProps
    });
    const dpaAnchor = /*#__PURE__*/ _jsx("a", {
        href: manufacturer.supportDpa,
        ...externalLinkProps
    });
    // Shared type-size + color so the mixed checkbox / info stack reads as
    // one cohesive list. Values are applied to both the info rows AND to each
    // Checkbox via `style`, because Antd's Checkbox label ignores font styles
    // that sit only on the parent Form.Item.
    const TEACHING_FONT_SIZE = 14;
    const TEACHING_LINE_HEIGHT = 1.5;
    const TEACHING_COLOR = token.colorText;
    const infoIcon = /*#__PURE__*/ _jsx(InfoCircleOutlined, {
        style: {
            color: TEACHING_COLOR,
            marginRight: 8,
            marginTop: 4
        }
    });
    const infoRowStyle = {
        display: "flex",
        alignItems: "flex-start",
        fontSize: TEACHING_FONT_SIZE,
        color: TEACHING_COLOR,
        marginBottom: 8,
        lineHeight: TEACHING_LINE_HEIGHT
    };
    const checkboxItemStyle = {
        marginBottom: 8
    };
    const checkboxStyle = {
        fontSize: TEACHING_FONT_SIZE,
        lineHeight: TEACHING_LINE_HEIGHT,
        color: TEACHING_COLOR
    };
    const termsText = isIndividual ? _i(__("I agree to the {{paddle}}Paddle Terms{{/paddle}} as seller and the {{devowl}}devowl.io Terms{{/devowl}} as product manufacturer."), {
        paddle: paddleTermsAnchor,
        devowl: devowlTermsAnchor
    }) : _i(__("The {{paddle}}Paddle Terms{{/paddle}} as seller and the {{devowl}}devowl.io Terms{{/devowl}} as product manufacturer apply."), {
        paddle: paddleTermsAnchor,
        devowl: devowlTermsAnchor
    });
    const privacyText = _i(__("The {{paddle}}Paddle Privacy Policy{{/paddle}} as seller and the {{devowl}}devowl.io Privacy Policy{{/devowl}} as product manufacturer apply."), {
        paddle: paddlePrivacyAnchor,
        devowl: devowlPrivacyAnchor
    });
    // For company buyers the form needs `isTermsAccepted: true` so the submit
    // payload stays truthy without a visible checkbox. The outer Form's
    // `initialValues` is the single source of truth for hydration; the submit
    // handler also force-applies `true` server-side so this hidden field is
    // belt-and-braces only.
    const hiddenTermsForBusiness = !isIndividual && /*#__PURE__*/ _jsx(Form.Item, {
        name: "isTermsAccepted",
        valuePropName: "checked",
        hidden: true,
        noStyle: true,
        children: /*#__PURE__*/ _jsx(Checkbox, {})
    });
    const termsCheckbox = isIndividual && /*#__PURE__*/ _jsx(Form.Item, {
        name: "isTermsAccepted",
        valuePropName: "checked",
        style: checkboxItemStyle,
        rules: [
            {
                validator: (_rule, value)=>value ? Promise.resolve() : Promise.reject(new Error(__("Please accept the terms and conditions.")))
            }
        ],
        children: /*#__PURE__*/ _jsx(Checkbox, {
            style: checkboxStyle,
            children: termsText
        })
    });
    const exclusionCheckbox = showExclusionOfRevocation && /*#__PURE__*/ _jsx(Form.Item, {
        name: "exclusionOfRevocation",
        valuePropName: "checked",
        style: checkboxItemStyle,
        rules: [
            {
                validator: (_rule, value)=>value ? Promise.resolve() : Promise.reject(new Error(__("Please confirm the exclusion of the right of withdrawal.")))
            }
        ],
        children: /*#__PURE__*/ _jsx(Checkbox, {
            style: checkboxStyle,
            children: _i(__("I expressly request the digital content to be supplied before the withdrawal period ends, acknowledge that I lose my right of withdrawal once supply begins, and have taken note of the {{link}}cancellation policy{{/link}}."), {
                link: cancellationAnchor
            })
        })
    });
    const noLegalAdviceCheckbox = showNoLegalAdvice && /*#__PURE__*/ _jsx(Form.Item, {
        name: "noLegalAdviceContractWork",
        valuePropName: "checked",
        style: checkboxItemStyle,
        rules: [
            {
                validator: (_rule, value)=>value ? Promise.resolve() : Promise.reject(new Error(__("Please confirm you understand this is not legal advice.")))
            }
        ],
        children: /*#__PURE__*/ _jsx(Checkbox, {
            style: checkboxStyle,
            children: __("I acknowledge that this service is provided with due care but does not constitute legal advice.")
        })
    });
    const dpaCheckbox = showDpaForPayedSupport && /*#__PURE__*/ _jsx(Form.Item, {
        name: "dpaForPayedSupport",
        valuePropName: "checked",
        style: checkboxItemStyle,
        rules: [
            {
                validator: (_rule, value)=>value ? Promise.resolve() : Promise.reject(new Error(__("Please accept the data processing agreement.")))
            }
        ],
        children: /*#__PURE__*/ _jsx(Checkbox, {
            style: checkboxStyle,
            children: _i(__("I agree to the {{link}}data processing agreement{{/link}} and allow the access to my WordPress backend required to perform the order."), {
                link: dpaAnchor
            })
        })
    });
    const termsInfoRow = !isIndividual && /*#__PURE__*/ _jsxs("div", {
        style: infoRowStyle,
        children: [
            infoIcon,
            /*#__PURE__*/ _jsx("span", {
                children: termsText
            })
        ]
    });
    const privacyInfoRow = /*#__PURE__*/ _jsxs("div", {
        style: infoRowStyle,
        children: [
            infoIcon,
            /*#__PURE__*/ _jsx("span", {
                children: privacyText
            })
        ]
    });
    const cancellationInfoRow = showCancellationPolicy && /*#__PURE__*/ _jsxs("div", {
        style: infoRowStyle,
        children: [
            infoIcon,
            /*#__PURE__*/ _jsx("span", {
                children: _i(__("You have a statutory right of withdrawal for this purchase as explained in our {{link}}cancellation policy{{/link}}."), {
                    link: cancellationAnchor
                })
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("div", {
        style: {
            marginBottom: 16,
            fontSize: TEACHING_FONT_SIZE,
            lineHeight: TEACHING_LINE_HEIGHT
        },
        children: [
            hiddenTermsForBusiness,
            termsCheckbox,
            exclusionCheckbox,
            noLegalAdviceCheckbox,
            dpaCheckbox,
            termsInfoRow,
            privacyInfoRow,
            cancellationInfoRow
        ]
    });
};
export { CustomerTermsSection };
